import sortBy from 'lodash/sortBy';

import * as api from '@/api';
import * as azureB2CApi from '@/api/azureB2C';
import { deleteAuthTokens, putAuthTokens, setAzureB2CPolicyName } from '@/utils/localStorage/authentication';

import {
  GET_ACTIVE_PRO_ACCOUNTS_RESPONSE,
  SET_PRO_ACCOUNT,
  getUserInfo,
  selectSystem,
} from '@/components/AppInitializer/actions';

import { queryClient } from '@/config/queryClient';
import events, { eventEmitter } from '@/events';

import { isNewHistoryFeaturesEnabled } from '@/constants';
import { clearSingleHistoryChartData } from '@/pages/SystemSettings/components/SystemHistoryTab/utils';
import {
  removeFirstChart,
  removeSecondChart,
} from '@/pages/SystemSettings/components/SystemHistoryTabOld/localStorageUtils';
import { clearDashAlertStatus } from '@/utils/localStorage/alerts';
import { removeLastUsedSystemId } from '@/utils/localStorage/lastUsedSystem';
import { splitVatNumber } from '../EditServicePartner/utils';

export const LOGIN_FAILED = 'login/LOGIN_FAILED';

export const SET_USER_LOGGED_IN = 'login/SET_USER_LOGGED_IN';
export const setUserLoggedInAction = () => dispatch => {
  dispatch({ type: SET_USER_LOGGED_IN });
};

export const loginWithAzureB2CAction = code => async dispatch => {
  try {
    const response = await azureB2CApi.issueAzureB2CTokens(code, azureB2CApi.AZURE_B2C_POLICY_LOGIN);
    putAuthTokens(response.data);
    setAzureB2CPolicyName(azureB2CApi.AZURE_B2C_POLICY_LOGIN);

    const userProAccounts = await api.getActiveProAccountIds();
    dispatch({ type: GET_ACTIVE_PRO_ACCOUNTS_RESPONSE, activeProAccounts: userProAccounts.data });

    const organizations = sortBy(userProAccounts.data, 'servicePartnerName');

    if (userProAccounts.data.length === 0) {
      await getUserInfo()(dispatch); // Potentialy unnecesary action
    }

    setUserLoggedInAction()(dispatch);

    return userProAccounts.data;
  } catch (e) {
    if (e.response && e.response.status === 400 && e.response.data.error === 'invalid_grant') {
      dispatch({ type: LOGIN_FAILED, error: 'An error has occurred. Try again later.' });
      return null;
    }
  }

  dispatch({ type: LOGIN_FAILED, error: 'unknown' });
  return false;
};

export const logoutUser = async () => {
  eventEmitter.emit(events.inApp.auth.LOGOUT);

  deleteAuthTokens();

  removeLastUsedSystemId();
  clearDashAlertStatus();

  if (isNewHistoryFeaturesEnabled) {
    clearSingleHistoryChartData();
  } else {
    // TODO: Setup history.clearStoredCharts()
    removeFirstChart();
    removeSecondChart();
  }

  await queryClient.cancelQueries();
  queryClient.clear();

  // Generate logout link BEFORE clearing the policy name (policy name is required in the URL)
  const logoutLink = azureB2CApi.getAzureB2CLogoutLink();
  setAzureB2CPolicyName('');

  // Redirect to Azure B2C logout endpoint
  // Note: We do NOT dispatch LOGOUT_USER action. It's unnecessary because:
  // 1. Tokens are already deleted from localStorage
  // 2. The browser will navigate away immediately
  // 3. On next page load, AppInitializer will see no tokens and redirect to login
  // 4. Dispatching the action would cause components to re-render with null state before redirect
  window.location.href = logoutLink;
};

export const loginAsServicePartner =
  ({ servicePartner }) =>
  async dispatch => {
    try {
      await api.authorizeUserUnderProAccount(servicePartner.servicePartnerId);
      removeLastUsedSystemId();
      dispatch(selectSystem(null));
      await getUserInfo()(dispatch);
      dispatch({ type: SET_USER_LOGGED_IN });
      return true;
    } catch {
      return false;
    }
  };

export const UPDATE_PRO_ACCOUNT_AGREEMENTS_REQUEST = 'editServicePartner/UPDATE_PRO_ACCOUNT_AGREEMENTS_REQUEST';
export const UPDATE_PRO_ACCOUNT_AGREEMENTS_ERROR = 'editServicePartner/UPDATE_PRO_ACCOUNT_AGREEMENTS_ERROR';
export const updateServicePartnerAgreements = (servicePartnerId, agreements) => async dispatch => {
  dispatch({ type: UPDATE_PRO_ACCOUNT_AGREEMENTS_REQUEST });
  try {
    const request = {
      organizationId: servicePartnerId,
      toSAcceptedMyUptech: agreements.tosAcceptedMyUptech,
    };
    const patchResponse = await api.editProAccountInfo(request);

    if (patchResponse.status === 204) {
      const getResponse = await api.getProAccountInfo(servicePartnerId);

      if (getResponse.status === 200) {
        const servicePartner = splitVatNumber(getResponse.data);
        dispatch({ type: SET_PRO_ACCOUNT, servicePartner });
      } else {
        dispatch({ type: UPDATE_PRO_ACCOUNT_AGREEMENTS_ERROR });
      }
      return;
    }
    dispatch({ type: UPDATE_PRO_ACCOUNT_AGREEMENTS_ERROR });
  } catch (e) {
    dispatch({ type: UPDATE_PRO_ACCOUNT_AGREEMENTS_ERROR });
  }
};
